import type { AppState } from '@capacitor/app'
import type { PermissionState } from '../stores/useNotificationStore'

interface NotificationPermissionResumeOptions {
  readPermission: () => Promise<PermissionState>
  updatePermission: (permission: PermissionState) => void
  onActive: () => void | Promise<void>
  onPermissionError?: (error: unknown) => void
}

/**
 * 建立 App 回到前景時的通知權限同步處理器。
 *
 * 權限必須先更新，後續的通知排程檢查才能讀到系統的最新狀態。
 * 即使權限查詢失敗，仍會執行既有的前景恢復工作。
 */
export function createNotificationPermissionResumeHandler(
  options: NotificationPermissionResumeOptions
) {
  let generation = 0

  return async (state: AppState): Promise<void> => {
    const currentGeneration = ++generation

    if (!state.isActive) {
      return
    }

    try {
      const permission = await options.readPermission()
      if (currentGeneration !== generation) {
        return
      }
      options.updatePermission(permission)
    } catch (error) {
      if (currentGeneration !== generation) {
        return
      }
      options.onPermissionError?.(error)
    }

    if (currentGeneration !== generation) {
      return
    }
    await options.onActive()
  }
}
